import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import ConsumableCard from './components/ConsumableCard';
import AddConsumableModal from './components/AddConsumableModal';
import EditConsumableModal from './components/EditConsumableModal';
import RestockModal from './components/RestockModal';
import UsageHistoryModal from './components/UsageHistoryModal';
import StockAlerts from './components/StockAlerts';
import SupplierCard from './components/SupplierCard';
import AddSupplierModal from './components/AddSupplierModal';
import EditSupplierModal from './components/EditSupplierModal';

const InventoryManagement = () => {
  const navigate = useNavigate();
  const [consumables, setConsumables] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRestockModalOpen, setIsRestockModalOpen] = useState(false);
  const [isUsageHistoryModalOpen, setIsUsageHistoryModalOpen] = useState(false);
  const [selectedConsumable, setSelectedConsumable] = useState(null);
  const [activeTab, setActiveTab] = useState('consumables');
  
  // Add this block - missing state declarations
  const [isAddSupplierModalOpen, setIsAddSupplierModalOpen] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [isEditSupplierModalOpen, setIsEditSupplierModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // Load consumables and alerts
  useEffect(() => {
    loadData();
    subscribeToChanges();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      
      const [consumablesResult, alertsResult, suppliersResult] = await Promise.all([
        supabase?.from('consumables')?.select('*')?.order('name'),
        supabase?.from('restock_alerts')?.select('*, consumables(*)')?.eq('is_resolved', false)?.order('created_at', { ascending: false }),
        supabase?.from('suppliers')?.select('*, consumables(count)')?.order('name')
      ]);

      if (consumablesResult?.error) throw consumablesResult?.error;
      if (alertsResult?.error) throw alertsResult?.error;
      if (suppliersResult?.error) throw suppliersResult?.error;

      setConsumables(consumablesResult?.data || []);
      setAlerts(alertsResult?.data || []);
      setSuppliers((suppliersResult?.data || []).map(supplier => ({
        ...supplier,
        consumablesCount: supplier.consumables?.[0]?.count || 0
      })));
    } catch (err) {
      setError(err?.message || 'Erreur lors du chargement des données');
      console.error('Error loading inventory:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const subscribeToChanges = () => {
    const consumablesChannel = supabase?.channel('consumables-changes')?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'consumables'
      },
      () => {
        loadData();
      }
    )?.subscribe();

    const alertsChannel = supabase?.channel('alerts-changes')?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'restock_alerts'
      },
      () => {
        loadData();
      }
    )?.subscribe();

    const suppliersChannel = supabase?.channel('suppliers-changes')?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'suppliers'
      },
      () => {
        loadData();
      }
    )?.subscribe();

    return () => {
      supabase?.removeChannel(consumablesChannel);
      supabase?.removeChannel(alertsChannel);
      supabase?.removeChannel(suppliersChannel);
    };
  };

  const handleAddConsumable = async (data) => {
    try {
      const { error } = await supabase?.from('consumables')?.insert([data]);
      if (error) throw error;
      setIsAddModalOpen(false);
      loadData();
    } catch (err) {
      console.error('Error adding consumable:', err);
      alert('Erreur lors de l\'ajout du consommable');
    }
  };

  const handleEditConsumable = async (id, data) => {
    try {
      const { error } = await supabase?.from('consumables')?.update(data)?.eq('id', id);
      if (error) throw error;
      setIsEditModalOpen(false);
      setSelectedConsumable(null);
      loadData();
    } catch (err) {
      console.error('Error updating consumable:', err);
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleAddSupplier = async (data) => {
    try {
      const { error } = await supabase?.from('suppliers')?.insert([{
        ...data,
        is_active: true
      }]);
      if (error) throw error;
      setIsAddSupplierModalOpen(false);
      loadData();
    } catch (err) {
      console.error('Error adding supplier:', err);
      throw error;
    }
  };

  const handleEditSupplier = async (id, data) => {
    try {
      const { error } = await supabase?.from('suppliers')?.update(data)?.eq('id', id);
      if (error) throw error;
      setIsEditSupplierModalOpen(false);
      setSelectedSupplier(null);
      loadData();
    } catch (err) {
      console.error('Error updating supplier:', err);
      throw error;
    }
  };

  const handleDeleteSupplier = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce fournisseur ?')) return;
    
    try {
      const { error } = await supabase?.from('suppliers')?.delete()?.eq('id', id);
      if (error) throw error;
      loadData();
    } catch (err) {
      console.error('Error deleting supplier:', err);
      alert('Erreur lors de la suppression du fournisseur');
    }
  };

  const handleRestock = async (consumableId, quantity, notes) => {
    try {
      const consumable = consumables?.find(c => c?.id === consumableId);
      const newStock = consumable?.current_stock + quantity;
      
      const { error } = await supabase?.from('consumables')?.update({
        current_stock: newStock,
        updated_at: new Date()?.toISOString()
      })?.eq('id', consumableId);
      
      if (error) throw error;

      // Resolve alerts if stock is now above threshold
      if (newStock > consumable?.minimum_threshold) {
        await supabase?.from('restock_alerts')?.update({
          is_resolved: true,
          resolved_at: new Date()?.toISOString()
        })?.eq('consumable_id', consumableId)?.eq('is_resolved', false);
      }

      setIsRestockModalOpen(false);
      setSelectedConsumable(null);
      loadData();
    } catch (err) {
      console.error('Error restocking:', err);
      alert('Erreur lors du réapprovisionnement');
    }
  };

  const handleDeleteConsumable = async (id) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce consommable ?')) return;
    
    try {
      const { error } = await supabase?.from('consumables')?.delete()?.eq('id', id);
      if (error) throw error;
      loadData();
    } catch (err) {
      console.error('Error deleting consumable:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const handleLogout = () => {
    navigate('/login-authentication');
  };

  const filteredConsumables = consumables?.filter(item => {
    const matchesSearch = item?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         item?.reference_code?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    if (filterStatus === 'low') {
      return matchesSearch && item?.current_stock <= item?.minimum_threshold;
    }
    if (filterStatus === 'active') {
      return matchesSearch && item?.is_active;
    }
    if (filterStatus === 'inactive') {
      return matchesSearch && !item?.is_active;
    }
    return matchesSearch;
  });

  const stats = {
    total: consumables?.length,
    lowStock: consumables?.filter(c => c?.current_stock <= c?.minimum_threshold)?.length,
    outOfStock: consumables?.filter(c => c?.current_stock === 0)?.length,
    totalValue: consumables?.reduce((sum, c) => sum + (c?.current_stock * (c?.unit_price || 0)), 0)
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Chargement de l'inventaire...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader
        userRole="dispatcher"
        userName="Dispatcher Admin"
        onLogout={handleLogout}
      />

      <main className="main-content">
        <div className="container mx-auto px-4 py-6 max-w-[1600px]">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Gestion des Consommables</h1>
              <p className="text-muted-foreground">
                Gérez le stock et suivez l'utilisation des consommables
              </p>
            </div>
            <Button
              variant="default"
              size="lg"
              iconName="Plus"
              onClick={() => activeTab === 'consumables' ? setIsAddModalOpen(true) : setIsAddSupplierModalOpen(true)}
            >
              {activeTab === 'consumables' ? 'Ajouter un consommable' : 'Ajouter un fournisseur'}
            </Button>
          </div>

          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <p className="text-error">{error}</p>
              </div>
            </div>
          )}

          <div className="bg-card rounded-lg border border-border mb-6">
            <div className="flex border-b border-border">
              <button
                onClick={() => setActiveTab('consumables')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'consumables' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Icon name="Package" size={18} />
                  <span>Consommables</span>
                  <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                    {consumables?.length}
                  </span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('suppliers')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'suppliers' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Icon name="Building2" size={18} />
                  <span>Fournisseurs</span>
                  <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                    {suppliers?.length}
                  </span>
                </div>
              </button>
            </div>
          </div>

          {activeTab === 'consumables' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-card rounded-lg border border-border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total articles</p>
                      <p className="text-2xl font-bold text-foreground">{stats?.total}</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="Package" size={24} className="text-primary" />
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-lg border border-border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Stock faible</p>
                      <p className="text-2xl font-bold text-warning">{stats?.lowStock}</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
                      <Icon name="AlertTriangle" size={24} className="text-warning" />
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-lg border border-border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Rupture de stock</p>
                      <p className="text-2xl font-bold text-error">{stats?.outOfStock}</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
                      <Icon name="XCircle" size={24} className="text-error" />
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-lg border border-border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Valeur totale</p>
                      <p className="text-2xl font-bold text-success">{stats?.totalValue?.toFixed(2)}€</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                      <Icon name="Euro" size={24} className="text-success" />
                    </div>
                  </div>
                </div>
              </div>

              {alerts?.length > 0 && (
                <StockAlerts alerts={alerts} onRestock={(consumable) => {
                  setSelectedConsumable(consumable);
                  setIsRestockModalOpen(true);
                }} />
              )}

              <div className="bg-card rounded-lg border border-border p-4 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Icon name="Search" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        placeholder="Rechercher par nom ou référence..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e?.target?.value)}
                        className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant={filterStatus === 'all' ? 'default' : 'outline'}
                      onClick={() => setFilterStatus('all')}
                    >
                      Tous
                    </Button>
                    <Button
                      variant={filterStatus === 'low' ? 'default' : 'outline'}
                      onClick={() => setFilterStatus('low')}
                    >
                      Stock faible
                    </Button>
                    <Button
                      variant={filterStatus === 'active' ? 'default' : 'outline'}
                      onClick={() => setFilterStatus('active')}
                    >
                      Actifs
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredConsumables?.map(consumable => (
                  <ConsumableCard
                    key={consumable?.id}
                    consumable={consumable}
                    onEdit={() => {
                      setSelectedConsumable(consumable);
                      setIsEditModalOpen(true);
                    }}
                    onRestock={() => {
                      setSelectedConsumable(consumable);
                      setIsRestockModalOpen(true);
                    }}
                    onViewHistory={() => {
                      setSelectedConsumable(consumable);
                      setIsUsageHistoryModalOpen(true);
                    }}
                    onDelete={() => handleDeleteConsumable(consumable?.id)}
                  />
                ))}
              </div>

              {filteredConsumables?.length === 0 && (
                <div className="bg-card rounded-lg border border-border p-12 text-center">
                  <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">Aucun consommable trouvé</h3>
                  <p className="text-muted-foreground mb-4">
                    {searchTerm ? 'Aucun résultat pour votre recherche' : 'Commencez par ajouter des consommables à votre inventaire'}
                  </p>
                  {!searchTerm && (
                    <Button
                      variant="default"
                      iconName="Plus"
                      onClick={() => setIsAddModalOpen(true)}
                    >
                      Ajouter un consommable
                    </Button>
                  )}
                </div>
              )}
            </>
          )}

          {activeTab === 'suppliers' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {suppliers?.filter(s => s?.is_active)?.map(supplier => (
                  <SupplierCard
                    key={supplier?.id}
                    supplier={supplier}
                    consumablesCount={supplier?.consumablesCount || 0}
                    onEdit={() => {
                      setSelectedSupplier(supplier);
                      setIsEditSupplierModalOpen(true);
                    }}
                    onDelete={() => handleDeleteSupplier(supplier?.id)}
                  />
                ))}
              </div>

              {suppliers?.filter(s => s?.is_active)?.length === 0 && (
                <div className="bg-card rounded-lg border border-border p-12 text-center">
                  <Icon name="Building2" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">Aucun fournisseur trouvé</h3>
                  <p className="text-muted-foreground mb-4">
                    Commencez par ajouter des fournisseurs à votre base de données
                  </p>
                  <Button
                    variant="default"
                    iconName="Plus"
                    onClick={() => setIsAddSupplierModalOpen(true)}
                  >
                    Ajouter un fournisseur
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {isAddModalOpen && (
        <AddConsumableModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleAddConsumable}
        />
      )}

      {isEditModalOpen && selectedConsumable && (
        <EditConsumableModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedConsumable(null);
          }}
          consumable={selectedConsumable}
          onSave={handleEditConsumable}
        />
      )}

      {isRestockModalOpen && selectedConsumable && (
        <RestockModal
          isOpen={isRestockModalOpen}
          onClose={() => {
            setIsRestockModalOpen(false);
            setSelectedConsumable(null);
          }}
          consumable={selectedConsumable}
          onRestock={handleRestock}
        />
      )}

      {isUsageHistoryModalOpen && selectedConsumable && (
        <UsageHistoryModal
          isOpen={isUsageHistoryModalOpen}
          onClose={() => {
            setIsUsageHistoryModalOpen(false);
            setSelectedConsumable(null);
          }}
          consumable={selectedConsumable}
        />
      )}

      {isAddSupplierModalOpen && (
        <AddSupplierModal
          isOpen={isAddSupplierModalOpen}
          onClose={() => setIsAddSupplierModalOpen(false)}
          onAdd={handleAddSupplier}
        />
      )}

      {isEditSupplierModalOpen && selectedSupplier && (
        <EditSupplierModal
          isOpen={isEditSupplierModalOpen}
          onClose={() => {
            setIsEditSupplierModalOpen(false);
            setSelectedSupplier(null);
          }}
          supplier={selectedSupplier}
          onSave={handleEditSupplier}
        />
      )}
    </div>
  );
};

export default InventoryManagement;